import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../app/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../app/components/ui/sheet";
import { useIsMobile } from "../../../lib/useBreakpoint";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { getErrorMessage } from "../../../lib/laravelErrors";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";

export type ReceiveLine = {
  product_id: string;
  product_name: string;
  ordered_qty: string;
  received_qty: string;
  purchase_price: string;
};

function remainingQty(line: ReceiveLine): string {
  const o = Number.parseFloat(line.ordered_qty);
  const r = Number.parseFloat(line.received_qty);
  return Math.max(0, o - r).toFixed(3);
}

type RowState = { received: string; purchase_price: string; selling_price: string; mrp: string };

export function ReceiveItemsModal({
  open,
  onOpenChange,
  purchaseOrderId,
  lines,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string;
  lines: ReceiveLine[];
  onDone: () => void;
}) {
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const pending = useMemo(() => lines.filter((l) => Number.parseFloat(remainingQty(l)) > 0), [lines]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const next: Record<string, RowState> = {};
    for (const line of lines) {
      const rem = remainingQty(line);
      if (Number.parseFloat(rem) <= 0) continue;
      next[line.product_id] = {
        received: rem,
        purchase_price: line.purchase_price,
        selling_price: "0",
        mrp: "0",
      };
    }
    setRows(next);
  }, [open, lines]);

  function updateRow(productId: string, patch: Partial<RowState>) {
    setRows((prev) => {
      const cur = prev[productId];
      if (!cur) return prev;
      return { ...prev, [productId]: { ...cur, ...patch } };
    });
  }

  async function submit() {
    if (!token) return;
    setError(null);

    const items: Array<{
      product_id: string;
      received_qty: string;
      purchase_price?: string;
      selling_price: string;
      mrp: string;
    }> = [];

    for (const line of pending) {
      const row = rows[line.product_id];
      if (!row) continue;
      const recv = row.received.trim();
      if (!recv || Number.parseFloat(recv) <= 0) continue;
      const max = Number.parseFloat(remainingQty(line));
      if (Number.parseFloat(recv) > max + 1e-9) {
        setError(`Received qty too high for ${line.product_name || line.product_id}`);
        return;
      }
      const payload: (typeof items)[number] = {
        product_id: line.product_id,
        received_qty: recv,
        selling_price: row.selling_price,
        mrp: row.mrp,
      };
      if (row.purchase_price.trim() !== "" && row.purchase_price !== line.purchase_price) {
        payload.purchase_price = row.purchase_price;
      }
      items.push(payload);
    }

    if (items.length === 0) {
      setError("Enter at least one positive received quantity.");
      return;
    }

    setSubmitting(true);
    try {
      await api.purchaseOrders.receivePOItems({ token, id: purchaseOrderId, items });
      trackEvent(TRACKING_EVENTS.PO_RECEIVED, { line_count: items.length });
      onOpenChange(false);
      onDone();
    } catch (e) {
      setError(getErrorMessage(e, "Receive failed"));
    } finally {
      setSubmitting(false);
    }
  }

  const tableSection =
    pending.length === 0 ? (
      <div className="text-sm text-muted-foreground">Nothing left to receive.</div>
    ) : (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Receive qty</TableHead>
              <TableHead>Purchase (override)</TableHead>
              <TableHead>Selling</TableHead>
              <TableHead>MRP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pending.map((line) => {
              const pid = line.product_id;
              const row = rows[pid];
              if (!row) return null;
              return (
                <TableRow key={pid}>
                  <TableCell className="font-medium">{line.product_name || pid}</TableCell>
                  <TableCell>{remainingQty(line)}</TableCell>
                  <TableCell>
                    <Input value={row.received} onChange={(e) => updateRow(pid, { received: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.purchase_price}
                      onChange={(e) => updateRow(pid, { purchase_price: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.selling_price}
                      onChange={(e) => updateRow(pid, { selling_price: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Input value={row.mrp} onChange={(e) => updateRow(pid, { mrp: e.target.value })} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        Cancel
      </Button>
      <Button type="button" onClick={() => void submit()} disabled={submitting || pending.length === 0}>
        {submitting ? "Receiving..." : "Receive"}
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex max-h-[92vh] flex-col gap-4 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Receive stock</SheetTitle>
          </SheetHeader>
          {error ? <div className="text-sm text-destructive">{error}</div> : null}
          {tableSection}
          <SheetFooter className="flex-row flex-wrap gap-2">{actions}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receive stock</DialogTitle>
        </DialogHeader>
        {error ? <div className="text-sm text-destructive">{error}</div> : null}
        {tableSection}
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
